import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ConfigService } from "../config.service";
import { PotentialGift, PotentialGiftWithContacts } from "./commonInterfaces";
import { Subject, switchMap, tap } from "rxjs";
import { DateTime } from "luxon";
import { DataTransform, DateField } from "./dataTransformFunctions";


export interface PotentialGiftUpdatedArgs {
    potentialGiftId: number,
    action: "create" | "update" | "delete"
}


export const potentialGiftStatusLabels = {
    1: "Identification",
    2: "Cultivation",
    3: "Proposal Submitted",
    4: "Pending",
    5: "Committed (Verbal)",
    6: "Committed (Written)",
    7: "Received",
    8: "Declined",
    9: "Deferred"
}

export const potentialGiftFrequencyLabels = {
    1: "Recurring",
    2: "One Time"
}

@Injectable({
    providedIn: 'root'
})
export class PotentialGiftsService {

    private potentialGiftUpdatedSubject = new Subject<PotentialGiftUpdatedArgs>()
    potentialGiftUpdated$ = this.potentialGiftUpdatedSubject.asObservable()

    readonly potentialGiftDateFields: DateField<PotentialGift>[] = [
        { fieldName: "anticipatedGivingDate", dateType: "local" },
    ]


    constructor(
        private http: HttpClient,
        private config: ConfigService
    ) { }


    getForCustomer(customerId: number) {
        return this.http.get<PotentialGiftWithContacts[]>(`${this.config.dataApiUrl}/potential-gifts/customer/${customerId}`).pipe(
            switchMap((potentialGifts) => DataTransform.replaceDatesArray(potentialGifts, this.potentialGiftDateFields))
        );
    }

    createPotentialGift(potentialGift: Omit<PotentialGift, "id">, contactIds: number[]) {

        const postData = {
            potentialGift: { ...potentialGift, anticipatedGivingDate: potentialGift.anticipatedGivingDate ? DateTime.fromJSDate(potentialGift.anticipatedGivingDate).toFormat("yyyy-MM-dd") : undefined },
            contactIds: contactIds
        }

        return this.http.post<PotentialGiftWithContacts>(`${this.config.dataApiUrl}/potential-gifts`, postData).pipe(
            tap((potentialGift) => {
                this.potentialGiftUpdatedSubject.next({ potentialGiftId: potentialGift.id, action: "create" })
            }),
            switchMap((potentialGift) => DataTransform.replaceDates(potentialGift, this.potentialGiftDateFields))
        );
    }

    updatePotentialGift(potentialGiftId: number, potentialGift: Omit<PotentialGift, "id">, contactIds: number[]) {

        const postData = {
            potentialGift: { ...potentialGift, anticipatedGivingDate: potentialGift.anticipatedGivingDate ? DateTime.fromJSDate(potentialGift.anticipatedGivingDate).toFormat("yyyy-MM-dd") : undefined },
            contactIds: contactIds
        }

        return this.http.put<PotentialGiftWithContacts>(`${this.config.dataApiUrl}/potential-gifts/${potentialGiftId}`, postData).pipe(
            tap((potentialGift) => {
                this.potentialGiftUpdatedSubject.next({ potentialGiftId: potentialGift.id, action: "update" })
            }),
            switchMap((potentialGift) => DataTransform.replaceDates(potentialGift, this.potentialGiftDateFields))
        );
    }

}